/**
 * 組織成員維護服務 (tenant_users)
 *
 * 使用便捷端點 /api/users/me/members
 * 自動使用當前使用者的組織 ID，不需要在呼叫時傳遞
 */

import axios from '../api/axios';
import { TenantUser, TenantUserCreate, TenantUserUpdate } from '../types/tenantUsers';

// E6b-d：挑人專用的最小揭露選項（只名字與帳號）。走登入者可用端點·非管理權限。
export interface MemberOption {
  id: number;
  username: string;
  account: string;
}

/**
 * 組織成員挑選器選項（會議室/日曆/分享挑人用）。
 * 登入者可用（self_scope）·限本組織、啟用中·只回 id/username/account。
 * 使用者「管理」仍走 getTenantUsers()（/me/members·需 tenant_users 權限）。
 */
export const getMemberOptions = async (q?: string): Promise<MemberOption[]> => {
  const response = await axios.get<MemberOption[]>('/api/users/me/member-options',
    { params: q ? { q } : undefined });
  return response.data;
};

/**
 * 取得當前組織的成員列表
 *
 * 使用便捷端點: GET /api/users/me/members
 * axios 攔截器會自動添加 X-Txn-Token header
 */
export const getTenantUsers = async (params?: {
  skip?: number;
  limit?: number;
  is_active?: boolean;
  search?: string;
}): Promise<TenantUser[]> => {
  try {
    const response = await axios.get<TenantUser[]>('/api/users/me/members', { params });
    return response.data;
  } catch (error) {
    console.error('Failed to get tenant users');
    throw error;
  }
};

/**
 * 取得單一組織成員資料
 *
 * 使用便捷端點: GET /api/users/me/members/{id}
 * axios 攔截器會自動添加 X-Txn-Token header
 */
export const getTenantUser = async (id: number): Promise<TenantUser> => {
  try {
    const response = await axios.get<TenantUser>(`/api/users/me/members/${id}`);
    return response.data;
  } catch (error) {
    console.error('Failed to get tenant user');
    throw error;
  }
};

/**
 * 新增組織成員
 *
 * 使用便捷端點: POST /api/users/me/members
 * 不需要傳遞 organization_id，後端會自動使用當前使用者的組織
 * axios 攔截器會自動添加 X-Txn-Token header
 */
export const createTenantUser = async (data: TenantUserCreate): Promise<TenantUser> => {
  try {
    const response = await axios.post<TenantUser>('/api/users/me/members', data);
    return response.data;
  } catch (error) {
    console.error('Failed to create tenant user');
    throw error;
  }
};

/**
 * 更新組織成員資料
 *
 * 使用便捷端點: PUT /api/users/me/members/{id}
 * 只能更新自己組織的成員
 * axios 攔截器會自動添加 X-Txn-Token header
 */
export const updateTenantUser = async (id: number, data: TenantUserUpdate): Promise<TenantUser> => {
  try {
    const response = await axios.put<TenantUser>(`/api/users/me/members/${id}`, data);
    return response.data;
  } catch (error) {
    console.error('Failed to update tenant user');
    throw error;
  }
};

/** email 變更紀錄（唯讀·原則 2）：舊值、新值、時間、變更人、來源。 */
export interface EmailChange {
  id: number;
  old_email: string | null;
  new_email: string | null;
  changed_by: number | null;
  source: string;
  created_at: string | null;
}
export const getUserEmailChanges = async (userId: number): Promise<EmailChange[]> => {
  const response = await axios.get<EmailChange[]>(`/api/users/${userId}/email-changes`);
  return response.data;
};

/**
 * 刪除組織成員（軟刪除）
 *
 * 使用便捷端點: DELETE /api/users/me/members/{id}
 * axios 攔截器會自動添加 X-Txn-Token header (one_time_use)
 */
export const deleteTenantUser = async (id: number): Promise<void> => {
  try {
    await axios.delete(`/api/users/me/members/${id}`);
  } catch (error) {
    console.error('Failed to delete tenant user');
    throw error;
  }
};

/**
 * 重設使用者密碼（發送郵件通知）
 */
export const resetUserPassword = async (userId: number): Promise<void> => {
  await axios.post(`/api/users/${userId}/reset-password`);
};

/**
 * 切換使用者啟用狀態
 */
export const toggleUserStatus = async (id: number, isActive: boolean): Promise<TenantUser> => {
  const response = await axios.put<TenantUser>(`/api/users/me/members/${id}`, { is_active: isActive });
  return response.data;
};

/**
 * 檢查帳號唯一性（用於編輯時驗證）
 */
export const checkAccountUniqueness = async (account: string, excludeId?: number): Promise<boolean> => {
  try {
    const users = await getTenantUsers();
    const existingUser = users.find(u => u.account === account && u.id !== excludeId);
    return !existingUser; // true = 唯一，false = 已存在
  } catch (error) {
    console.error('Failed to check account uniqueness');
    return false;
  }
};
